import WorkCreditShop from '@/components/Credits/WorkCreditShop';
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { StyleSheet, View, ActivityIndicator, Platform } from 'react-native';
import ModalLayout from './ModalLayout';
import { useAuthStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useExperiment } from '@/hooks/useExperiment';
import { trackEvent } from '@/constants/utils/analytics';
import useTranslation from '@/hooks/useTranslation';

import CreditsPurchaseModalControl from '@/components/Credits/CreditsPurchaseModalControl';
import CreditsPurchaseModalSpeed from '@/components/Credits/CreditsPurchaseModalSpeed';
import CreditsPurchaseModalStore from '@/components/Credits/CreditsPurchaseModalStore';
import CreditsPurchaseModalUrgent from '@/components/Credits/CreditsPurchaseModalUrgent';
import { checkIsIapSubscriber } from '@/lib/purchaseUtils';

const VARIANT_OPTIONS = {
  work: { width: 580, height: 740, heightMobile: [740, '92%'] },
  control: { width: 880, height: 840, heightMobile: [840, '92%'] },
  speed:   { width: 600, height: 620, heightMobile: [680, '92%'] },
  store:   { width: 900, height: 500, heightMobile: [760, '92%'] },
  urgent:  { width: 470, height: 500, heightMobile: [660, '92%'] },
};

const createStyles = () => StyleSheet.create({
  centeredContainer: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
});

function CreditsPurchaseModal({ onClose, initialTab, onDimensionsChange, promoCode, workContext, requiredCredits }) {
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(), []);

  // control, store, speed, urgent
  const experimentVariant = useExperiment('credits_purchase_modal_v2');
  const variant = workContext ? 'work' : experimentVariant;

  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(activePurchase),
    [activePurchase],
  );
  const credits = useAuthStore(state => state.user?.credits);
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable) ?? 0;

  const variantOpts = VARIANT_OPTIONS[variant] || VARIANT_OPTIONS.control;

  useEffect(() => {
    if (onDimensionsChange) {
      onDimensionsChange(variantOpts);
    }
  }, [variant, variantOpts, onDimensionsChange]);

  const [variantLowerActions, setVariantLowerActions] = useState([]);
  const handleVariantLowerActions = useCallback((actions) => {
    setVariantLowerActions(actions);
  }, []);

  useEffect(() => {
    trackEvent('conversion', 'credits_purchase_modal_viewed', isPlusPremium ? 'plus_premium' : isPlus ? 'plus' : 'free');
    trackEvent('conversion', 'credits_modal_variant', variant);
  }, []);

  if (!credits) {
    return (
      <View style={styles.centeredContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }
  
  if (isGhost) {
    !!onClose && onClose();
    openModal('GhostSignupModal');
    return null;
  }

  const monthlyCredits = credits.monthlyAllotment;
  const purchasedCredits = credits.purchased;
  const hasMonthlyCredits = isPlus && monthlyCredits;
  const hasPurchasedCredits = purchasedCredits?.available > 0;

  const sharedProps = {
    isPlus,
    isPlusPremium,
    isIapSubscriber,
    credits,
    onClose,
    hasMonthlyCredits,
    hasPurchasedCredits,
    monthlyCredits,
    purchasedCredits,
    totalAvailableCredits,
    initialTab,
    promoCode,
  };

  const renderVariant = () => {
    switch (variant) {
      case 'speed':
        return <CreditsPurchaseModalSpeed {...sharedProps} />;
      case 'store':
        return <CreditsPurchaseModalStore {...sharedProps} onLowerActionsChange={handleVariantLowerActions} />;
      case 'urgent':
        return <CreditsPurchaseModalUrgent {...sharedProps} onLowerActionsChange={handleVariantLowerActions} />;
      default:
        return <CreditsPurchaseModalControl {...sharedProps} />;
    }
  };

  const isControl = !['work', 'speed', 'store', 'urgent'].includes(variant);
  const baseLowerActions = !isControl && isXS && Platform.OS !== 'macos' ? [
    {
      title: t('modals.creditsPurchase.close'),
      onPress: onClose,
    }
  ] : [];

  const hasVariantActions = variant === 'store' || variant === 'urgent';
  const lowerActions = hasVariantActions
    ? [...variantLowerActions, ...baseLowerActions]
    : baseLowerActions;

  if (workContext) return (
    <View style={{ flex: 1 }} onLayout={() => onDimensionsChange?.(VARIANT_OPTIONS.work)}>
      <ModalLayout title="Add credits" titleBorder={false} onClose={onClose}>
        <WorkCreditShop onClose={onClose} requiredCredits={requiredCredits} />
      </ModalLayout>
    </View>
  );

  return (
    <ModalLayout
      hideCloseButton={isControl || Platform.OS !== 'macos'}
      title={!isControl && Platform.OS === 'macos' ? t('modals.creditsPurchase.buyCreditsTab') : undefined}
      onClose={onClose}
      lowerActions={lowerActions}
      background='primary'
    >
      {renderVariant()}
    </ModalLayout>
  );
} 

CreditsPurchaseModal.modalOptions = {
  width: 880,
  height: 840,
  heightMobile: [840, '92%'],
  background: 'primary',
};

export default CreditsPurchaseModal;
